import {useDisclosure} from '@mantine/hooks';
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import {useAuth} from '../../../core/usecases/auth/AuthContext';
import {isStudentVerified as checkStudentVerified} from '../../../core/usecases/auth/isStudentVerified';
import {NotStudentVerifiedAlertModal} from '../../../shared/components/NotStudentVerifiedAlertModal';

type RequireStudentVerificationContextType = {
  /** 大学認証が完了しているか。副作用がないためレンダー中に参照できる */
  isStudentVerified: boolean;
  /**
   * 大学認証状態をチェックし、未認証時はモーダルを表示する
   * @returns 大学認証済みならtrue、未認証ならfalse（モーダル表示後）
   */
  requireStudentVerification: () => boolean;
  /**
   * 大学認証が必要なアクションをラップする
   * 未認証時はアクションを実行せずモーダルを表示
   */
  withRequireStudentVerification: <T extends (...args: any[]) => any>(
    action: T,
  ) => (...args: Parameters<T>) => ReturnType<T> | undefined;
};

const RequireStudentVerificationContext =
  createContext<RequireStudentVerificationContextType | null>(null);

/**
 * 大学認証が必要なアクションを管理するProvider
 * EntryManagementLayout内で使用し、NotStudentVerifiedAlertModalを一元管理
 */
export const RequireStudentVerificationProvider = ({
  children,
}: PropsWithChildren) => {
  const {user} = useAuth();
  const [opened, {open, close}] = useDisclosure(false);

  const isStudentVerified = checkStudentVerified(user);

  const requireStudentVerification = useCallback((): boolean => {
    if (isStudentVerified) {
      return true;
    }
    open();
    return false;
  }, [isStudentVerified, open]);

  const withRequireStudentVerification = useCallback(
    <T extends (...args: any[]) => any>(
      action: T,
    ): ((...args: Parameters<T>) => ReturnType<T> | undefined) => {
      return (...args: Parameters<T>): ReturnType<T> | undefined => {
        if (!requireStudentVerification()) {
          return undefined;
        }
        return action(...args);
      };
    },
    [requireStudentVerification],
  );

  const value = useMemo(
    () => ({
      isStudentVerified,
      requireStudentVerification,
      withRequireStudentVerification,
    }),
    [
      isStudentVerified,
      requireStudentVerification,
      withRequireStudentVerification,
    ],
  );

  return (
    <RequireStudentVerificationContext.Provider value={value}>
      {children}
      <NotStudentVerifiedAlertModal opened={opened} onClose={close} />
    </RequireStudentVerificationContext.Provider>
  );
};

/**
 * 大学認証が必要なアクションを管理するフック
 * EntryManagement配下のコンポーネントで使用
 */
export const useRequireStudentVerification =
  (): RequireStudentVerificationContextType => {
    const context = useContext(RequireStudentVerificationContext);
    if (!context) {
      throw new Error(
        'useRequireStudentVerification must be used within RequireStudentVerificationProvider',
      );
    }
    return context;
  };
